// use-run-detail-state — derives unified step display state from REST run data
// merged with live SSE stream state. Keeps RunDetailContent under 200 LoC.

import { useMemo } from "react";
import type { WorkflowRunStep, StepStatus } from "@/types/workflow.types";
import type { StepStreamState } from "@/hooks/useWorkflowRunStream";
import type { StorySectionState } from "@/components/ui/story-section/story-section";

// ── Step status → StorySection state mapping ──────────────────────────────────
// 5 BE step states map 1:1 to 5 StorySection visual states:
//   done → done
//   running / thinking / tool_running → active  (ringPulse breathing halo)
//   pending / queued / waiting / waiting_checkpoint → pending
//   failed → failed
//   paused / skipped_by_decision → warning

export function stepStatusToStoryState(status: StepStatus): StorySectionState {
  switch (status) {
    case "success":
      return "done";
    case "running":
    case "thinking":
    case "tool_running":
      return "active";
    case "failed":
      return "failed";
    case "paused":
    case "skipped_by_decision":
    case "skipped":
      return "warning";
    case "pending":
    case "waiting":
    case "waiting_checkpoint":
    case "cancelled":
    default:
      return "pending";
  }
}

// ── Merged step type ──────────────────────────────────────────────────────────

export interface MergedStep {
  id: string;
  order: number;
  stepName: string;
  stepType: string;
  /** Resolved status — stream takes precedence over REST. */
  status: StepStatus;
  storyState: StorySectionState;
  progressPercent: number;
  progressMessage?: string;
  durationMs?: number;
  errorMessage?: string;
  iterCount?: number;
  outputText?: string;
  decisionLabel?: string;
  decisionReasoning?: string;
  costUsd?: number;
  inputCount?: number;
  outputCount?: number;
  metadata?: Record<string, unknown>;
}

interface UseRunDetailStateOptions {
  restSteps: WorkflowRunStep[];
  streamSteps: Record<string, StepStreamState>;
}

function numericMetadataValue(metadata: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = metadata?.[key];
  return typeof value === "number" ? value : undefined;
}

function outputText(outputData: Record<string, unknown> | undefined): string | undefined {
  const text = outputData?.text;
  return typeof text === "string" && text.length > 0 ? text : undefined;
}

export function useRunDetailState({
  restSteps,
  streamSteps,
}: UseRunDetailStateOptions): MergedStep[] {
  return useMemo(() => {
    return restSteps
      .slice()
      .sort((a, b) => {
        const aOrder = a.step?.stepOrder ?? 0;
        const bOrder = b.step?.stepOrder ?? 0;
        return aOrder - bOrder;
      })
      .map((rs): MergedStep => {
        const ss = streamSteps[rs.stepId];
        // Stream state wins on status; fall back to REST for fields not in stream.
        const status: StepStatus = ss?.status ?? rs.status;
        return {
          id: rs.stepId,
          order: rs.step?.stepOrder ?? 0,
          stepName: rs.step?.stepName ?? "",
          stepType: rs.step?.stepType ?? "unknown",
          status,
          storyState: stepStatusToStoryState(status),
          progressPercent: ss?.progressPercent ?? rs.progressPercent ?? 0,
          progressMessage: ss?.progressMessage ?? rs.progressMessage,
          durationMs: ss?.durationMs ?? rs.durationMs,
          errorMessage: ss?.errorMessage ?? rs.errorMessage,
          iterCount: ss?.iterCount ?? numericMetadataValue(rs.metadata, "iterations_used"),
          outputText: ss?.outputText ?? outputText(rs.outputData),
          decisionLabel: ss?.decisionLabel,
          decisionReasoning: ss?.decisionReasoning,
          costUsd: ss?.costUsd ?? numericMetadataValue(rs.metadata, "cost_usd"),
          inputCount: ss?.inputCount ?? rs.inputCount,
          outputCount: ss?.outputCount ?? rs.outputCount,
          metadata: rs.metadata,
        };
      });
  }, [restSteps, streamSteps]);
}
